import config, { routePaths } from 'appConfig';
import {
  Button,
  Image,
  Link,
  Skeleton,
  TextBalancer,
} from 'components/common';
import { useAuth } from 'lib/auth';
import { useRouter } from 'next/router';
import useMobile from 'hooks/useMobile';
import { Form, FormManager } from 'lib/form';
import validator from 'lib/validator';
import { TextField } from 'components/formFields';
import useLocalStorage from 'hooks/useLocalStorage';
import httpClient from 'lib/httpClient';
import notification from 'lib/notification';
import { FormikHelpers } from 'formik';

const GlobalBanner = () => {
  const isMobile = useMobile(768);
  const bannerImage = isMobile
    ? '/assets/images/common/global-bottom-banner-mobile-new.png'
    : '/assets/images/common/global-bottom-banner.png';
  const size = isMobile
    ? { width: 1200, height: 970 }
    : { width: 2000, height: 800 };

  return (
    <div className="tw-relative">
      <Image
        src={bannerImage}
        alt="global bottom banner"
        layout="responsive"
        width={size.width}
        height={size.height}
        objectFit="cover"
      />
      <div className="tw-absolute tw-inset-0">
        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-h-full tw-space-y-5 xl:tw-space-y-8 tw-max-w-xl lg:tw-max-w-3xl xl:tw-max-w-4xl tw-mx-auto tw-pt-8">
          <div className="tw-flex tw-flex-col tw-justify-center tw-items-center">
            <h1 className="tw-font-therebrat tw-text-white tw-mb-0 tw-leading-tight tw-tracking-wide tw-font-medium tw-whitespace-nowrap tw-text-[3.25rem] md:tw-text-[3.75rem] lg:tw-text-[4.5rem] xl:tw-text-[5rem] tw-text-shadow tw-text-center">
              New To
            </h1>
            <h1 className="tw-text-white tw-mb-0 tw-leading-[0.8] tw-text-[3.75rem] md:tw-text-[4.5rem] lg:tw-text-[5rem] xl:tw-text-[6rem] tw-text-shadow tw-text-center">
              <span className="tw-uppercase tw-font-black">
                Yummy Bros
              </span>
              <span className="tw-font-therebrat tw-ml-2">?</span>
            </h1>
          </div>
          <TextBalancer
            as="h5"
            className="tw-text-white tw-mb-0 tw-font-medium tw-leading-snug tw-text-[1.5rem] md:tw-text-[1.75rem] xl:tw-text-[2rem] tw-text-shadow tw-text-center"
          >
            Enjoy <strong>$10 OFF + FREE Delivery</strong> when you
            spend $50 or more with code{' '}
            <strong>{config.welcomeCouponCode}</strong> at checkout.
          </TextBalancer>
          <div className="tw-flex tw-items-center tw-gap-x-4">
            <Link to={routePaths.CATEGORIES}>
              <Button className="tw-uppercase">
                <>Order Now</>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

interface EmailGrabFormValues {
  email: string;
}

const EmailGrabBanner = () => {
  const isMobile = useMobile(1024);
  const bannerImage = isMobile
    ? '/assets/images/common/email-grab-bottom-banner-mobile.png'
    : '/assets/images/common/email-grab-bottom-banner-new.png';
  const size = isMobile
    ? { width: 1200, height: 1130 }
    : { width: 2500, height: 829 };
  const NUTRITION_DIET_GUIDE_SUBSCRIPTION_LOCAL_STORAGE_KEY =
    'nutrition_diet_guide_subscription';

  const initialValues: EmailGrabFormValues = {
    email: '',
  };

  const validationSchema = validator.object({
    email: validator
      .string()
      .email()
      .required('Aiyo, need to fill this in first!'),
  });

  const [submittedEmail, setSubmittedEmail] = useLocalStorage<
    string | null
  >(NUTRITION_DIET_GUIDE_SUBSCRIPTION_LOCAL_STORAGE_KEY, null);

  const handleOnSubmit = async (
    values: EmailGrabFormValues,
    actions: FormikHelpers<EmailGrabFormValues>,
  ) => {
    try {
      await httpClient.post('/events/mail_grab', { ...values });
      setSubmittedEmail(values.email);
      actions.resetForm();
    } catch (error) {
      notification.error(error.message ?? 'Something went wrong.');
    }
  };

  return (
    <div className="tw-relative">
      <Image
        src={bannerImage}
        alt="global bottom banner"
        layout="responsive"
        width={size.width}
        height={size.height}
        objectFit="cover"
      />
      <div className="tw-absolute tw-inset-0 tw-grid tw-grid-cols-1 lg:tw-grid-cols-9">
        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center lg:tw-col-start-6 lg:tw-col-span-3">
          <div className="tw-max-w-lg lg:tw-max-w-3xl xl:tw-max-w-4xl tw-flex tw-flex-col tw-space-y-5 xl:tw-space-y-8 tw-pt-4 sm:tw-pt-16 md:tw-pt-20 lg:tw-pt-8 xl:tw-pt-16">
            <div className="tw-flex tw-flex-col">
              <h1 className="tw-font-therebrat tw-text-white tw-mb-0 tw-leading-tight tw-tracking-wide tw-font-medium tw-whitespace-nowrap tw-text-[3.25rem] md:tw-text-[3.75rem] lg:tw-text-[4.5rem] xl:tw-text-[5rem] tw-text-shadow tw-text-center">
                Get&nbsp; Your Free
              </h1>
              <h1 className="tw-text-white tw-mb-0 tw-leading-[0.8] tw-text-[3.75rem] md:tw-text-[4.5rem] lg:tw-text-[5rem] xl:tw-text-[6rem] tw-text-shadow tw-text-center">
                <span className="tw-uppercase tw-font-black">
                  Nutrition &
                  <br />
                  Diet Guide
                </span>
                <span className="tw-font-therebrat tw-ml-2">!</span>
              </h1>
            </div>
            <TextBalancer
              as="h5"
              className="tw-text-white tw-mb-0 tw-font-medium tw-leading-snug tw-text-[1.35rem] md:tw-text-[1.5rem] xl:tw-text-[1.8rem] tw-text-shadow tw-text-center"
            >
              {submittedEmail ? (
                <>
                  You’re in! Your guide is on its way to{' '}
                  <strong>{submittedEmail}</strong>
                  <br />
                  Want to subscribe with another email? Just fill in
                  the form below and hit Sign Me Up again!
                </>
              ) : (
                <>
                  Sign up and get simple, science-backed tips that
                  help you burn fat, build muscle, stay energised, and
                  take the guesswork out of eating well — all without
                  giving up the flavours you love!
                </>
              )}
            </TextBalancer>
            <div className="tw-flex tw-w-full tw-justify-center">
              {/* @ts-ignore */}
              <FormManager
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleOnSubmit}
              >
                {({ isSubmitting, values }) => (
                  <Form className="tw-w-4/5 tw-space-y-4">
                    {/* @ts-ignore */}
                    <TextField
                      name="email"
                      type="email"
                      placeholder="Your email, please!"
                      className="tw-bg-white tw-text-center"
                      formGroupProps={{ className: 'tw-mb-0' }}
                      helperProps={{
                        className: 'tw-mb-0 tw-text-center',
                      }}
                    />

                    <Button
                      type="submit"
                      loading={isSubmitting}
                      disabled={
                        isSubmitting || !Boolean(values.email)
                      }
                      className="tw-uppercase tw-w-full tw-font-semibold"
                    >
                      <>Sign Me Up!</>
                    </Button>
                  </Form>
                )}
              </FormManager>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BottomBanner = () => {
  const { session, loading } = useAuth();
  const { pathname } = useRouter();

  if (loading) {
    return (
      <Skeleton rectWidth="100%" options={{ lineHeight: 320 }} />
    );
  }

  if (session) return null;

  const excludedPaths = [routePaths.CATERING, routePaths.YB_EXPRESS];

  if (excludedPaths.includes(pathname)) return null;

  const emailGrabPaths = [
    routePaths.OUR_STORY,
    routePaths.THE_YB_EFFECT,
    routePaths.CAUSES_WE_SUPPORTED,
    routePaths.CONTACT_US,
    routePaths.DOORSTEP_DELIVERY,
    routePaths.CLICK_AND_COLLECT,
    routePaths.MEALCARE,
    routePaths.WEIGHT_LOSS_TIPS,
    routePaths.IS_YUMMY_BROS_HALAL,
    routePaths.YB_REPS,
    routePaths.MY_FITNESS_PAL,
    routePaths.WEEKLY_MEAL_PLANNER,
    routePaths.GIFT_CARDS,
  ];

  if (emailGrabPaths.includes(pathname)) return <EmailGrabBanner />;

  return <GlobalBanner />;
};

export default BottomBanner;
